<template>
  <div class="map-indoor-controller" />
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters, mapActions } from 'vuex'
import { FUNCTION_TYPE, MAP_LAYER_REFERENCE_TABLE_TYPE } from '@/constants.js'
import IFieldRasterTileRenderer from '@/js/map-renderer/IFieldRasterTileRenderer.js'
import BackdropRenderer from '@/js/map-renderer/BackdropRenderer.js'

/**
 * 屋内マップ管理コンポーネント
 * NOTE:
 *  - 屋内マップの管理を行う
 *  - マップレイヤー、マップスタイル一覧を取得して、Store に設定
 */
export default {
  name: 'AppMapIndoorController',
  props: {
    /**
     * マップレイヤー
     */
    mapLayers: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState('map', ['filter']),
    ...mapState('mapIndoor', ['isClickBuildingPinToActive']),
    ...mapGetters('map', ['layerState']),
    ...mapGetters('mapLayer', [
      'selectedIndoorBuilding',
      // ...
    ]),
    ...mapGetters('mapIndoor', [
      'activeIndoorBuildingId',
      'activeIndoorLevelId',
      'isGlobalMode',
      // ...
    ]),
    /**
     * 屋内に関するマップレイヤーのID
     * @type {Array.<Number>}
     */
    indoorMapLayerIds: function () {
      return [
        MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_SITE,
        MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_BUILDING,
      ]
    },
    /**
     * タイル(屋内レベル)レイヤーのスタイル
     * @type {Object}
     */
    rasterTileStyle: function () {
      const originalStyle =
        this.layerState.style[IFieldRasterTileRenderer.LAYER_ID] || {}
      return {
        id: IFieldRasterTileRenderer.LAYER_ID,
        style: {
          ...originalStyle,
          indoorLevelId: this.activeIndoorLevelId,
        },
      }
    },
    /**
     * バックドロップの表示状態
     * @type {Object}
     */
    backdropVisible: function () {
      return {
        id: BackdropRenderer.LAYER_ID,
        visible: !this.isGlobalMode,
      }
    },
  },
  mounted: function () {
    // スタイルを設定する
    this.setStyle([this.rasterTileStyle])
    // バックドロップの表示設定
    this.setVisible([this.backdropVisible])
    // フィルタ設定
    this.$_setIndoorLevelFilter()
  },
  watch: {
    mapLayers: function () {
      this.$_setIndoorLevelFilter()
      this.$_updateIndoorLayerVisible()
    },
    activeIndoorBuildingId: function (newValue, oldValue) {
      /**
       * ビル変更イベント(rootイベント)
       * @param {Number} id ビルID
       */
      this.$root.$emit('indoor-building-changed', newValue)

      this.$_onUpdateBuilding(newValue, oldValue)
    },
    activeIndoorLevelId: function (newValue) {
      /**
       * フロア変更イベント(rootイベント)
       * @param {Number} id フロアID
       */
      this.$root.$emit('indoor-level-changed', newValue)

      // 選択されている屋内レベルの設定
      this.setStyle([this.rasterTileStyle])
      // バックドロップの表示設定
      this.setVisible([this.backdropVisible])
      // フィルタ設定
      this.$_setIndoorLevelFilter()
      // Indoor の Layer Visible 自動制御
      this.$_updateIndoorLayerVisible()
    },
    selectedIndoorBuilding(value) {
      if (value) {
        this.$_onSelectedIndoorBuilding(value)
      }
    },
  },
  methods: {
    ...mapActions('map', ['setStyle', 'setVisible', 'setFilter']),

    /**
     * 屋内建物クリック (info deck.gl clickCallback)
     * @param {Object} object deck.gl clickCallback
     */
    $_onSelectedIndoorBuilding(object) {
      const props = object.properties
      if (
        !this.isClickBuildingPinToActive &&
        object.geometry &&
        object.geometry.type === 'Point'
      ) {
        // PIN をクリック時には屋内モードへ移動しない (Popup の処理をなどを表示側にまかせる)
        return
      }

      this.$_onUpdateBuilding(
        props.indoor_building_id,
        this.activeIndoorBuildingId
      )
    },

    /**
     * 屋内建物の更新された
     * @param {Number} newId 更新後屋内建物ID
     * @param {Number} oldId 更新前屋内建物ID
     */
    $_onUpdateBuilding(newId, oldId) {
      if (!newId) {
        // 未選択状態になった
        this.$store.dispatch('mapIndoor/unselectedBuilding')
        return
      }

      let building =
        this.$store.getters['indoorBuilding/getIndoorBuildingById'](newId)
      if (!building) {
        // 権限が無い
        this.$alert.fire({
          icon: 'error',
          title: this.$t('appMapIndoorController.message.notAllow'),
          text: this.$t('appMapIndoorController.message.noPermission'),
        })
        // 屋内建物IDを元に戻す
        // 他コンポーネントの描画更新ライフサイクル分は待つ
        this.$nextTick().then(() =>
          this.$store.commit('mapIndoor/setActiveIndoorBuildingId', oldId)
        )
      } else if (!building.indoor_levels || !building.indoor_levels.length) {
        // フロアがない
        this.$alert.fire({
          icon: 'error',
          title: this.$t('appMapIndoorController.message.notAllow'),
          text: this.$t('appMapIndoorController.message.noFloor'),
        })
        // 屋内建物IDを元に戻す
        // 他コンポーネントの描画更新ライフサイクル分は待つ
        this.$nextTick().then(() =>
          this.$store.commit('mapIndoor/setActiveIndoorBuildingId', oldId)
        )
      } else {
        // 建物リストの更新（一つだけだけど）
        this.$store.dispatch('mapIndoor/changeIndoorBuilding', building)
      }
    },

    /**
     * 屋内レベルフィルタを設定する
     */
    $_setIndoorLevelFilter: function () {
      if (!this.filter) {
        return
      }

      let filter = _.cloneDeep(this.filter)
      for (const layer of this.mapLayers) {
        if (layer.reference_table_type !== +FUNCTION_TYPE.PLACE) continue
        if (filter[`${layer.id}`]) {
          filter[`${layer.id}`].indoor_level_id = this.activeIndoorLevelId
        } else {
          filter[`${layer.id}`] = {
            indoor_level_id: this.activeIndoorLevelId,
          }
        }
      }
      filter = Object.entries(filter).map(([key, value]) => ({
        id: key,
        filter: value,
      }))
      this.setFilter(filter)
    },

    /**
     * 屋内の選択状態による、Visible の制御
     */
    $_updateIndoorLayerVisible() {
      // !FIXME: 仕様として正しいか？
      // Indoor Level が選択されている場合に
      // 上部に Indoor Building (Polygon, Point) が表示されてしまう
      // Indoor Level が選択されている場合は、表示を OFF にする
      const levelId = this.activeIndoorLevelId
      const visible = Object.keys(this.layerState.visible).map((key) => {
        const mapLayer = this.mapLayers.find((layer) => `${layer.id}` === key)
        if (
          mapLayer &&
          this.indoorMapLayerIds.includes(mapLayer.reference_table_type)
        ) {
          // 屋内建物 (屋内レベルの選択と逆の表示)
          const v = !levelId
            ? {
                geometry: true,
                label: true,
              }
            : false
          return { id: key, visible: v }
        } else {
          // 他のものは現在の設定値をそのまま利用
          return { id: key, visible: this.layerState.visible[key] }
        }
      })
      this.setVisible(visible)
    },
  },
}
</script>
